import { useState, useEffect, useRef } from "react";
import type { RefObject } from "react";
import { Container, Nav, Tab } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { InfoCircle } from "react-bootstrap-icons";
import VisaoGeralTab from "./Tabs/VisaoGeralTab/VisaoGeralTab";
import CadastroTab from "./Tabs/CadastroTab/CadastroTab";
import DocObrigatoriosTab from "./Tabs/DocObrigatoriosTab/DocObrigatoriosTab";
import DocBonusTab from "./Tabs/DocBonusTab/DocBonusTab";
import DocBonusExtraTab from "./Tabs/DocBonusExtraTab/DocBonusExtraTab";
import NotificationToast from "../../../../components/NotificationToast/NotificationToast";
import IndiceCriterios from "../../../../components/IndiceCriterios/IndiceCriterios";
import {
  getRespostaAtivaByLoggedUser,
  getQuestionarioRespostaById,
  getSindicatoByLoggedUser,
  getDocumentosByRespostaId,
} from "../../../../services";
import { PATHS } from "../../../../routes/paths";
import { scrollContentAreaToElement } from "utils/scrollContentArea";
import "./ShippingV1.css";

const tabs = [
  { id: "visao-geral", label: "VISÃO GERAL" },
  { id: "cadastro", label: "CADASTRO" },
  { id: "doc-obrigatorios", label: "DOC. OBRIGATÓRIOS" },
  { id: "doc-bonus", label: "DOC. BÔNUS" },
  { id: "doc-extra-bonus", label: "DOC. BÔNUS EXTRA" },
];

const indiceItems = [
  {
    title: "DOCUMENTOS OBRIGATÓRIOS",
    links: [
      {
        tabKey: "doc-obrigatorios",
        id: "doc-obrigatorios-curso-conselheiros",
        label: "CURSO DE CONSELHEIROS",
      },
      {
        tabKey: "doc-obrigatorios",
        id: "doc-obrigatorios-dre",
        label: "DRE - DEMONSTRAÇÕES DO RESULTADO",
      },
      {
        tabKey: "doc-obrigatorios",
        id: "doc-obrigatorios-ata-assembleia",
        label: "ATA DA ASSEMBLEIA DE PRESTAÇÃO DE CONTAS",
      },
      {
        tabKey: "doc-obrigatorios",
        id: "doc-obrigatorios-relacao-empresas",
        label: "RELAÇÕES DE EMPRESAS ASSOCIADAS",
      },
      {
        tabKey: "doc-obrigatorios",
        id: "doc-obrigatorios-adimplencia",
        label: "ADIMPLÊNCIA FINANCEIRA DAS EMPRESAS ASSOCIADAS",
      },
    ],
  },
  {
    title: "DOCUMENTOS BÔNUS",
    links: [
      {
        tabKey: "doc-bonus",
        id: "doc-bonus-plano-negocio",
        label: "PLANO DE NEGÓCIO SINDICAL",
      },
      {
        tabKey: "doc-bonus",
        id: "doc-bonus-renovacao-lideranca",
        label: "RENOVAÇÃO DA LIDERANÇA",
      },
      {
        tabKey: "doc-bonus",
        id: "doc-bonus-balanco",
        label: "BALANÇO PATRIMONIAL",
      },
      {
        tabKey: "doc-bonus",
        id: "doc-bonus-eventos",
        label: "EVENTOS PROMOVIDOS PELO SINDICATO",
      },
      {
        tabKey: "doc-bonus",
        id: "doc-bonus-hi-gestor",
        label: "UTILIZAÇÃO TOTAL DO SISTEMA HI GESTOR",
      },
    ],
  },
  {
    title: "DOCUMENTOS BÔNUS EXTRA",
    links: [
      {
        tabKey: "doc-extra-bonus",
        id: "doc-extra-acao-internacional",
        label: "AÇÃO INTERNACIONAL",
      },
      {
        tabKey: "doc-extra-bonus",
        id: "doc-extra-defesa-interesse",
        label: "DEFESA DE INTERESSE IMPLEMENTADA PELO EXECUTIVO OU LEGISLATIVO",
      },
      {
        tabKey: "doc-extra-bonus",
        id: "doc-extra-fiemg-jovem",
        label: "INDICAÇÃO DE MEMBROS PARA O PROGRAMA FIEMG JOVEM",
      },
    ],
  },
];

type TopicShape = {
  idCampoJson: string;
};

type ToastState = {
  show: boolean;
  message: string;
  type: string;
};

type TabRefHandle = {
  handleSave?: () => Promise<{
    show?: boolean;
    message?: string;
    type?: string;
  }>;
};

type RespostaComId = {
  data?: {
    id?: string | number;
  };
};

type ShippingV1LocationState = {
  idQuestionarioResposta?: number | null;
  mode?: string;
};

const docTabKeys = ["doc-obrigatorios", "doc-bonus", "doc-extra-bonus"] as const;

const resolveIdRespostaFromNavigation = (
  state: ShippingV1LocationState | null | undefined,
): number | null => {
  const raw = state?.idQuestionarioResposta;
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
};

const ShippingV1 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("visao-geral");
  const [resposta, setResposta] = useState<unknown>(null);
  const [sindicato, setSindicato] = useState<unknown>(null);
  const [documentos, setDocumentos] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: "",
    type: "",
  });

  const [isIndiceOpen, setIsIndiceOpen] = useState(false);

  const [openByTab, setOpenByTab] = useState<Record<string, Record<string, boolean>>>({
    "doc-obrigatorios": {},
    "doc-bonus": {},
    "doc-extra-bonus": {},
  });

  const [topicsByTab, setTopicsByTab] = useState<Record<string, TopicShape[]>>({
    "doc-obrigatorios": [],
    "doc-bonus": [],
    "doc-extra-bonus": [],
  });

  const cadastroRef = useRef<TabRefHandle>(null);
  const obrigatoriosRef = useRef<TabRefHandle>(null);
  const bonusRef = useRef<TabRefHandle>(null);
  const bonusExtraRef = useRef<TabRefHandle>(null);

  const refs: Record<string, RefObject<TabRefHandle> | undefined> = {
    cadastro: cadastroRef,
    "doc-obrigatorios": obrigatoriosRef,
    "doc-bonus": bonusRef,
    "doc-extra-bonus": bonusExtraRef,
    "visao-geral": undefined,
  };

  useEffect(() => {
    const carregarDadosIniciais = async () => {
      setLoading(true);
      try {
        const navState = (location.state ?? null) as ShippingV1LocationState | null;
        const idLista = resolveIdRespostaFromNavigation(navState);
        const respostaData =
          idLista != null
            ? await getQuestionarioRespostaById(idLista)
            : await getRespostaAtivaByLoggedUser();
        const sindicatoData = await getSindicatoByLoggedUser();

        setResposta(respostaData);
        setSindicato(sindicatoData);

        const respostaId =
          (respostaData as RespostaComId)?.data?.id ?? (idLista != null ? String(idLista) : "");
        const documentosData = await getDocumentosByRespostaId(respostaId);

        setDocumentos(Array.isArray(documentosData) ? documentosData : []);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais", error);
      } finally {
        setLoading(false);
      }
    };

    void carregarDadosIniciais();
  }, [location.key]);

  const handleTabChange = async (nextTab: string | null) => {
    if (!nextTab) return;
    if (nextTab === activeTab) return;

    setLoading(true);

    try {
      const currentRef = refs[activeTab];

      if (currentRef?.current?.handleSave) {
        const result = await currentRef.current.handleSave();

        if (result?.show) {
          setToast({
            show: true,
            message: result.message ?? "",
            type: result.type ?? "",
          });
        }
      }

      const navState = (location.state ?? null) as ShippingV1LocationState | null;
      const idLista = resolveIdRespostaFromNavigation(navState);
      const respostaAtualizada =
        idLista != null
          ? await getQuestionarioRespostaById(idLista)
          : await getRespostaAtivaByLoggedUser();
      const respostaId =
        (respostaAtualizada as RespostaComId)?.data?.id ??
        (idLista != null ? String(idLista) : "");
      const documentosAtualizados = await getDocumentosByRespostaId(respostaId);

      setResposta(respostaAtualizada);
      setDocumentos(Array.isArray(documentosAtualizados) ? documentosAtualizados : []);

      localStorage.removeItem("arquivosTemporarios");
      setActiveTab(nextTab);
    } catch (error) {
      console.error("❌ Erro ao salvar ao trocar de aba", error);
      setToast({
        show: true,
        message: "Erro ao salvar os dados da aba atual.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const canToggleAll =
    docTabKeys.includes(activeTab as (typeof docTabKeys)[number]) &&
    (topicsByTab[activeTab]?.length ?? 0) > 0;

  const isAllOpenOnTab = () => {
    if (!canToggleAll) return false;

    const topics = topicsByTab[activeTab] ?? [];
    const map = openByTab[activeTab] ?? {};

    return topics.every((topic) => map[topic.idCampoJson] === true);
  };

  const toggleAllOnTab = () => {
    if (!canToggleAll) return;

    const topics = topicsByTab[activeTab] ?? [];
    const nextValue = !isAllOpenOnTab();

    setOpenByTab((prev) => {
      const nextTabState = { ...(prev[activeTab] ?? {}) };

      topics.forEach((topic) => {
        nextTabState[topic.idCampoJson] = nextValue;
      });

      return {
        ...prev,
        [activeTab]: nextTabState,
      };
    });
  };

  type IndiceLink = {
    tabKey?: string;
    id?: string;
    label?: string;
    path?: string;
  };

  const handleIndiceNavigateAsync = async (link: IndiceLink) => {
    if (!link?.id) return;

    const goScroll = () => {
      setTimeout(() => {
        const el = document.getElementById(link.id!);
        if (el) {
          scrollContentAreaToElement(el, { behavior: "smooth" });
        } else {
          console.warn("Anchor não encontrado:", link.id);
        }
      }, 60);
    };

    try {
      if (link.tabKey && link.tabKey !== activeTab) {
        await handleTabChange(link.tabKey);
        goScroll();
        return;
      }

      goScroll();
    } catch (error) {
      console.error("Erro ao navegar pelo índice", error);
    }
  };

  const handleIndiceNavigate = (link: { path?: string }) => {
    void handleIndiceNavigateAsync(link as IndiceLink);
  };

  if (loading) return <PageLoading minHeight="80vh" />;

  return (
    <Container className="shipping-container" style={{ position: "relative" }}>
      <div
        className="merit-voltar-seta"
        title="Voltar para a lista"
        onClick={() => navigate(PATHS.DOC_SHIPPING)}
        style={{
          position: "absolute",
          top: "16px",
          right: "15px",
          zIndex: 20,
        }}
      >
        <FaArrowLeft />
      </div>

      <NotificationToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />

      <IndiceCriterios
        open={isIndiceOpen}
        onClose={() => setIsIndiceOpen(false)}
        items={indiceItems}
        onNavigate={handleIndiceNavigate}
      />

      <button
        type="button"
        onClick={() => setIsIndiceOpen(true)}
        className="shipping-index-btn shipping-index-btn--floating"
        aria-label="Abrir índice de critérios"
        title="Índice de critérios"
      >
        <InfoCircle className="shipping-index-btn-icon" aria-hidden size={22} />
      </button>

      <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
        <div className="shipping-tabs-row" data-merit-ic-below>
          <Nav variant="tabs" className="shipping-tabs">
            {tabs.map((tab) => (
              <Nav.Item key={tab.id}>
                <Nav.Link eventKey={tab.id} className="shipping-tab">
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        <Tab.Content className="shipping-content">
          <Tab.Pane eventKey="visao-geral">
            <VisaoGeralTab
              resposta={resposta}
              sindicatoData={sindicato}
              documentos={documentos}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="cadastro">
            <CadastroTab
              ref={cadastroRef}
              isActive={activeTab === "cadastro"}
              setActiveTab={setActiveTab}
              resposta={resposta}
              sindicatoData={sindicato}
              onProximo={() => handleTabChange("doc-obrigatorios")}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-obrigatorios">
            <DocObrigatoriosTab
              ref={obrigatoriosRef}
              isActive={activeTab === "doc-obrigatorios"}
              setActiveTab={setActiveTab}
              resposta={resposta}
              documentos={documentos}
              onProximo={() => handleTabChange("doc-bonus")}
              openByTopic={openByTab["doc-obrigatorios"] ?? {}}
              onToggleOne={(idCampoJson: string) =>
                setOpenByTab((prev) => ({
                  ...prev,
                  "doc-obrigatorios": {
                    ...(prev["doc-obrigatorios"] ?? {}),
                    [idCampoJson]: !(prev["doc-obrigatorios"] ?? {})[idCampoJson],
                  },
                }))
              }
              onTopicsLoaded={(topics: TopicShape[]) =>
                setTopicsByTab((prev) => ({
                  ...prev,
                  "doc-obrigatorios": topics.map((topic) => ({
                    idCampoJson: topic.idCampoJson,
                  })),
                }))
              }
              showToggleAll={canToggleAll}
              isAllOpen={isAllOpenOnTab()}
              onToggleAll={toggleAllOnTab}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-bonus">
            <DocBonusTab
              ref={bonusRef}
              isActive={activeTab === "doc-bonus"}
              setActiveTab={setActiveTab}
              resposta={resposta}
              documentos={documentos}
              onProximo={() => handleTabChange("doc-extra-bonus")}
              openByTopic={openByTab["doc-bonus"] ?? {}}
              onToggleOne={(idCampoJson: string) =>
                setOpenByTab((prev) => ({
                  ...prev,
                  "doc-bonus": {
                    ...(prev["doc-bonus"] ?? {}),
                    [idCampoJson]: !(prev["doc-bonus"] ?? {})[idCampoJson],
                  },
                }))
              }
              onTopicsLoaded={(topics: TopicShape[]) =>
                setTopicsByTab((prev) => ({
                  ...prev,
                  "doc-bonus": topics.map((topic) => ({
                    idCampoJson: topic.idCampoJson,
                  })),
                }))
              }
              showToggleAll={canToggleAll}
              isAllOpen={isAllOpenOnTab()}
              onToggleAll={toggleAllOnTab}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="doc-extra-bonus">
            <DocBonusExtraTab
              ref={bonusExtraRef}
              isActive={activeTab === "doc-extra-bonus"}
              setActiveTab={setActiveTab}
              resposta={resposta}
              documentos={documentos}
              openByTopic={openByTab["doc-extra-bonus"] ?? {}}
              onToggleOne={(idCampoJson: string) =>
                setOpenByTab((prev) => ({
                  ...prev,
                  "doc-extra-bonus": {
                    ...(prev["doc-extra-bonus"] ?? {}),
                    [idCampoJson]: !(prev["doc-extra-bonus"] ?? {})[idCampoJson],
                  },
                }))
              }
              onTopicsLoaded={(topics: TopicShape[]) =>
                setTopicsByTab((prev) => ({
                  ...prev,
                  "doc-extra-bonus": topics.map((topic) => ({
                    idCampoJson: topic.idCampoJson,
                  })),
                }))
              }
              showToggleAll={canToggleAll}
              isAllOpen={isAllOpenOnTab()}
              onToggleAll={toggleAllOnTab}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default ShippingV1;